import { useState } from "react";
import { LucideIcon, ChevronDown } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { useIsMobile } from "@/hooks/use-mobile";

interface SidebarItem {
  id: string;
  label: string;
  icon: LucideIcon;
}

interface DeviceSidebarProps {
  title: string;
  items: SidebarItem[];
  activeSection: string;
  onSectionChange: (sectionId: string) => void;
  isBrowserBlocked?: boolean;
  isWhitelistedBrowser?: boolean;
  onBrowserStateChange?: (allowBrowser: boolean, whitelistOnly?: boolean) => Promise<void>;
}

export const DeviceSidebar = ({ 
  title, 
  items, 
  activeSection, 
  onSectionChange, 
  isBrowserBlocked = false,
  isWhitelistedBrowser = false,
  onBrowserStateChange
}: DeviceSidebarProps) => {
  const [showBrowserWarning, setShowBrowserWarning] = useState(false);
  const [showWhitelistWarning, setShowWhitelistWarning] = useState(false);
  const isMobile = useIsMobile();

  const activeItem = items.find(item => item.id === activeSection);

  const handleBrowserSelectionChange = (value: string) => {
    if (value === "no" && !isBrowserBlocked) {
      setShowBrowserWarning(true);
    } else if (value === "whitelist" && !isWhitelistedBrowser) {
      setShowWhitelistWarning(true);
    } else if (value === "yes" && (isBrowserBlocked || isWhitelistedBrowser)) {
      onBrowserStateChange?.(true, false);
    }
  };

  const confirmBrowserBlock = () => {
    onBrowserStateChange?.(false, false);
    setShowBrowserWarning(false);
  };

  const confirmWhitelistBrowser = () => {
    onBrowserStateChange?.(true, true);
    setShowWhitelistWarning(false);
  };

  return (
    <>
      {isMobile ? (
        /* Mobile Dropdown */
        <div className="w-full bg-gray-50 border-b border-gray-200 p-2 space-y-2">
          <h3 className="text-xs font-medium text-gray-900">{title}</h3>
          
          {/* Section Selector */}
          <Select value={activeSection} onValueChange={onSectionChange}>
            <SelectTrigger className="w-full h-7 text-xs">
              <SelectValue>
                {activeItem?.label || "Select section"}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {items.map((item) => {
                const Icon = item.icon;
                return (
                  <SelectItem key={item.id} value={item.id}>
                    <div className="flex items-center gap-1">
                      <Icon className="w-3 h-3" />
                      <span className="text-xs">{item.label}</span>
                    </div>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>
      ) : (
        /* Desktop Sidebar */
        <div className="min-w-fit bg-gray-50 border-r border-gray-200 h-full flex flex-col">
          <div className="p-2 flex-1">
            <h3 className="text-xs font-medium text-gray-900 mb-2 whitespace-nowrap">{title}</h3>
            <nav className="space-y-0.5">
              {items.map((item) => {
                const Icon = item.icon;
                const isActive = activeSection === item.id;
                
                return (
                  <button
                    key={item.id}
                    onClick={() => onSectionChange(item.id)}
                    className={`
                      w-full flex items-center px-2 py-1 text-xs font-medium rounded transition-colors whitespace-nowrap
                      ${isActive 
                        ? 'bg-blue-100 text-blue-700' 
                        : 'text-gray-700 hover:bg-gray-100'
                      }
                    `}
                  >
                    <Icon className="w-3 h-3 mr-1.5 flex-shrink-0" />
                    {item.label}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>
      )}


      {/* Browser Block Warning Dialog */}
      <AlertDialog open={showBrowserWarning} onOpenChange={setShowBrowserWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Block Browser Access?</AlertDialogTitle>
            <AlertDialogDescription>
              This will block all browser access on this device. The user will not be able to browse the internet.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmBrowserBlock}>
              Block Browser
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Whitelist Browser Warning Dialog */}
      <AlertDialog open={showWhitelistWarning} onOpenChange={setShowWhitelistWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Enable Whitelisted Browser?</AlertDialogTitle>
            <AlertDialogDescription>
              This will restrict browser access to only whitelisted websites. The user will only be able to browse sites that have been specifically approved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmWhitelistBrowser}>
              Enable Whitelisted Browser
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};